<template>
  <div>
    <div class="row q-gutter-xs">
      <q-input
        class="col"
        :dark="isDarkModeGlobal"
        ref="PulseDur"
        for="PulseDur"
        filled
        dense
        label="Pulse Duration [ms]"
        type="number"
        step="0.000001"
        min="0"
        v-model="pulse_duration_ms"
        :rules="timingRules"
        hide-bottom-space
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Duration of single continuous sonication including ramps
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>
    </div>
  </div>

  <div>
    <div class="row q-gutter-xs">
      <q-input
        class="col"
        :dark="isDarkModeGlobal"
        ref="RampDur"
        for="RampDur"
        filled
        dense
        label="Pulse Ramp Duration [ms]"
        type="number"
        step="0.01"
        min="0"
        :max="pulse_duration_ms / 2"
        v-model="pulse_ramp_duration_ms"
        :rules="rampRules"
        hide-bottom-space
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Time from onset of pulse to attainment of maximum pressure within a pulse. Maximum for each pulse might
                differ if pulse train ramping is also applied.
                Must be equal or less than half the pulse duration.
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>

      <q-input
        class="col"
        :dark="isDarkModeGlobal"
        ref="percentageOfPD"
        for="percentageOfPD"
        filled
        dense
        label="% of PD"
        type="number"
        step="0.000001"
        min="0"
        max="50"
        v-model="percentage_of_pd"
        :rules="[
          (val) => val <= 50 || 'To prevent overlapping up and down ramping, the percentage of ramping is cut off at 50%'
        ]"
        @blur="checkPercrRampingValue"
        hide-bottom-space
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Pulse Ramp Duration can be calculated based on the percentage of the Pulse Duration.
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>
    </div>
  </div>
  <q-select
    class="col"
    :dark="isDarkModeGlobal"
    filled
    dense
    :options="rampShapes"
    label="Pulse Ramp Shape"
    type="text"
    v-model="pulse_ramp_shape"
  >
    <template #append>
      <q-btn
        icon="help"
        color="primary"
        size="sm"
        dense
        flat
      >
        <q-tooltip>
          Show note
        </q-tooltip>
        <q-popup-proxy :offset="[-70, 20]">
          <q-banner :dark="isDarkModeGlobal">
            <template #avatar>
              <q-icon
                name="help"
                color="primary"
              />
            </template>
            For example Linear or Tukey.
          </q-banner>
        </q-popup-proxy>
      </q-btn>
    </template>
  </q-select>
  <q-input
    class="col"
    :dark="isDarkModeGlobal"
    ref="DutyCycleOfPulse"
    for="DutyCycleOfPulse"
    outlined
    readonly
    dense
    label="Duty Cycle of Pulse"
    type="number"
    step="0.000001"
    min="0"
    v-model="calculatorStore.duty_cycle_of_pulse"
    hide-bottom-space
  />
</template>
<script setup lang="ts">
import {computed, ref, watch} from "vue"
import {storeToRefs} from "pinia"
import {useCalculatorStore} from '../stores/calculator.store'
import {formatNumber} from "./utils";

const calculatorStore = useCalculatorStore()
const {
  pulse_ramp_shape,
  pulse_duration_ms,
  pulse_ramp_duration_ms,
  pulse_repetition_interval_ms,
  pulse_train_duration_ms,
  pulse_train_repetition_interval_ms,
  pulse_train_repetition_duration_ms,
  time_parameters_valid,
  isDarkModeGlobal,
  percentage_of_pd,
  rampShapes,
  importingData
} = storeToRefs(calculatorStore)

const PulseDur = ref(null)
const RampDur = ref(null)
const percentageOfPD = ref(null)

// RULES
// Don't allow a Pulse Train Repetition Interval smaller than Pulse Train Duration (equal is allowed).
const ptri_gteq_ptd = computed(() => +pulse_train_repetition_interval_ms.value >= +pulse_train_duration_ms.value)
// The Pulse Repetition Interval should always be larger than the Pulse Duration.
const pri_gteq_pd = computed(() => +pulse_duration_ms.value > 0 && +pulse_repetition_interval_ms.value >= +pulse_duration_ms.value)
// Don't allow a Pulse Train Duration smaller than the Pulse Repetition Interval (equal is allowed).
const ptd_gteq_pri = computed(() => +pulse_train_duration_ms.value >= +pulse_repetition_interval_ms.value)
// Don't allow a Pulse Train Repetition Duration smaller than the Pulse Train Repetition Interval (equal is allowed).
const ptrd_gteq_ptri = computed(() => +pulse_train_repetition_duration_ms.value >= +pulse_train_repetition_interval_ms.value)
const ramp_duration_limit = computed(() => +pulse_duration_ms.value / 2)
const prd_isValid = computed(() => +pulse_ramp_duration_ms.value <= ramp_duration_limit.value)
const rampRules = [val => val >= 0 || 'Value must be >= 0',
      () => prd_isValid.value || 'Pulse Ramp Duration should always be equal or less than half the Pulse Duration.',
      () => ptrd_gteq_ptri.value || 'Pulse Train Repetition Duration must be larger than or equal to Pulse Train Repetition Interval',
      () => ptd_gteq_pri.value || 'Pulse Train Duration must be larger than or equal to Pulse Repetition Interval',
      () => ptri_gteq_ptd.value || 'Pulse Train Repetition Interval must be larger than or equal to Pulse Train Duration',
      () => pri_gteq_pd.value || 'Pulse Repetition Interval must be larger than or equal to Pulse Duration',]

const timingRules = [
      val => val > 0 || 'Value must be > 0',
      () => prd_isValid.value || 'Pulse Ramp Duration should always be equal or less than half the Pulse Duration.',
      () => ptrd_gteq_ptri.value || 'Pulse Train Repetition Duration must be larger than or equal to Pulse Train Repetition Interval',
      () => ptd_gteq_pri.value || 'Pulse Train Duration must be larger than or equal to Pulse Repetition Interval',
      () => ptri_gteq_ptd.value || 'Pulse Train Repetition Interval must be larger than or equal to Pulse Train Duration',
      () => pri_gteq_pd.value || 'Pulse Repetition Interval must be larger than or equal to Pulse Duration',
    ]

function checkPercrRampingValue() {
  if (importingData.value) return;

  if (percentage_of_pd.value > 50) {
      percentage_of_pd.value = 50
    } else {
      percentageOfPD.value.resetValidation()
    }

    pulse_ramp_duration_ms.value = formatNumber((+percentage_of_pd.value / 100) * +pulse_duration_ms.value)
    calculatorStore.showRecalculateButton()
}

watch(time_parameters_valid, (bool) => {
  if (bool) {
    PulseDur.value.resetValidation()
    RampDur.value.resetValidation()
    percentageOfPD.value.resetValidation()
  }
})

watch(pulse_duration_ms, (newVal) => {
  if (importingData.value) return;
  if (pulse_duration_ms.value > 0) {
    if (+newVal > +pulse_repetition_interval_ms.value) {
      pulse_repetition_interval_ms.value = +newVal
    }

    pulse_ramp_duration_ms.value = formatNumber((+percentage_of_pd.value / 100) * +pulse_duration_ms.value)
    calculatorStore.showRecalculateButton()
  }
})

watch(pulse_ramp_duration_ms, () => {
  if (importingData.value) return;
  percentage_of_pd.value = formatNumber((+pulse_ramp_duration_ms.value / +pulse_duration_ms.value) * 100)
  calculatorStore.showRecalculateButton()
})

watch(pulse_ramp_shape, () => calculatorStore.showRecalculateButton())
</script>